import BnumElementInternal from 'components/bnum-element-states';
import { HTMLBnumFragment } from '../fragment/bnum-fragment';
import { Attr, Data, Define, Fire } from 'core/decorators';
import { UI } from 'core/decorators/ui';
import { BasicDetail, ValueOf } from 'core/utils/types';
import { MayBe, Nullable, Optional } from '@rotomeca/utils';
import { HTMLBnumIcon } from '../icon/bnum-icon';
import { Result, Risky } from '@rotomeca/rop';
import { EMPTY_STRING } from '@rotomeca/utils';
import Log from 'core/utils/Log';
import { INPUT_BASE_STYLE, INPUT_STYLE_STATES } from '../input/bnum-input';
import style from './bnum-switch.less';
import { Listener } from 'core/decorators/listener';
import JsEvent from '@rotomeca/event';
import { NonStd } from 'core/decorators/nonstd';
import { BnumConfig } from 'core/utils/BnumConfig';

//#region Utilities

/**
 * Initialise l'écouteur d'événement `change` sur l'instance du checkbox.
 *
 * @remarks
 * Ajoute un listener natif `change` sur l'élément hôte et appelle le callback
 * de l'événement personnalisé {@link OnCheckedChangeEvent} lorsqu'il y a des abonnés.
 *
 * @param event - L'événement personnalisé à déclencher
 * @param instance - L'instance du composant {@link HTMLBnumSwitch}
 *
 * @internal
 */
function OnCheckedChangeInitializer(
	event: OnCheckedChangeEvent,
	instance: HTMLBnumSwitch,
) {
	instance.addEventListener(EVENT_CHANGE, (e: Event) => {
		if (event.haveEvents()) event.call(e);
	});
}

//#endregion Utilities

//#region Internal Types

/**
 * Interface définissant les références aux éléments UI du composant checkbox.
 *
 * @remarks
 * Utilisé par le décorateur {@link UI} pour injecter les références typées
 * aux éléments du Shadow DOM. Ces références permettent d'interagir avec
 * l'input natif, les slots et les éléments d'état.
 */
type BnumSwitchUi = {
	/** L'élément input checkbox natif */
	input: HTMLInputElement;
	/** Le span affichant le texte lorsque le checkbox est activé */
	textActive: HTMLSpanElement;
	/** Le span affichant le texte lorsque le checkbox est désactivé */
	textInactive: HTMLSpanElement;
	/** Le slot contenant la légende principale du checkbox */
	slotLegend: HTMLSlotElement;
	/** Le slot contenant l'indice/aide du checkbox */
	slotHint: HTMLSlotElement;
	/** Le span affichant le texte de validité */
	validityText: HTMLSpanElement;
	/** L'icône d'état du checkbox */
	icon: HTMLBnumIcon;
};

/**
 * Détails de l'événement de changement d'état du checkbox.
 *
 * @remarks
 * Contient l'événement natif sous-jacent et la référence à l'instance du composant.
 */
type BnumSwitchDetail = BasicDetail<HTMLBnumSwitch, Event>;

/**
 * Événement personnalisé déclenché lors du changement d'état du checkbox.
 */
type BnumSwitchChangeCustomEvent = CustomEvent<BnumSwitchDetail>;

/**
 * Signature du callback exécuté lors du changement d'état coché.
 *
 * @param event - L'événement personnalisé contenant les détails du changement
 */
type OnCheckedChangeCallback = (event: BnumSwitchChangeCustomEvent) => void;

/**
 * Type d'événement personnalisé pour le changement d'état coché.
 */
type OnCheckedChangeEvent = JsEvent<OnCheckedChangeCallback>;

//#endregion Internal Types

//#region Global Constants

/**
 * Icônes utilisées pour les états du checkbox.
 *
 * @remarks
 * Associe chaque état de validation à une icône Material Icons correspondante.
 *
 * @internal
 */
const BnumSwitchIcon = {
	SUCCESS: 'check_circle',
	ERROR: 'cancel',
} as const;

/**
 * Type des valeurs d'icônes possibles pour le checkbox.
 */
type BnumSwitchIcon = ValueOf<typeof BnumSwitchIcon>;

/**
 * Nom de l'attribut 'checked'.
 * @internal
 */
const ATTRIBUTE_CHECKED = 'checked';

/**
 * Nom de l'attribut 'helper'.
 * @internal
 */
const ATTRIBUTE_HELPER = 'helper';

/**
 * Nom de l'événement 'change'.
 * @internal
 */
const EVENT_CHANGE = 'change';

/**
 * Tag utilisé pour les messages de log du composant.
 * @internal
 */
const LOG_TAG = 'BnumCheckbox';

/**
 * Message d'avertissement affiché lorsqu'aucun label n'est trouvé.
 * @internal
 */
const WARN_NO_LABEL = "Aucun texte de description ou d'aide n'a été trouvé";

/**
 * Message de validité par défaut lorsqu'aucun message natif n'est disponible.
 * @internal
 */
const DEFAULT_VALIDITY_MESSAGE = 'Certaines conditions ne sont pas satisfaites';

/**
 * Nom de l'état interne 'state'.
 * @internal
 */
const STATE_STATE = 'state';

/**
 * Nom de l'état interne 'error'.
 * @internal
 */
const STATE_ERROR = 'error';

/**
 * Nom de l'attribut ARIA 'aria-checked'.
 * @internal
 */
const ARIA_CHECKED = 'aria-checked';

/**
 * Nom de l'attribut ARIA 'aria-required'.
 * @internal
 */
const ARIA_REQUIRED = 'aria-required';

/**
 * Nom de l'attribut ARIA 'aria-disabled'.
 * @internal
 */
const ARIA_DISABLED = 'aria-disabled';

/**
 * Nom de l'attribut ARIA 'aria-invalid'.
 * @internal
 */
const ARIA_INVALID = 'aria-invalid';

/**
 * Nom de l'attribut ARIA 'aria-describedby'.
 * @internal
 */
const ARIA_DESCRIBEDBY = 'aria-describedby';

/**
 * Identifiant du slot d'indice.
 * @internal
 */
const ID_HINT = 'hint';

/**
 * Identifiant de l'élément affichant le texte de validité.
 * @internal
 */
const ID_VALIDITY_TEXT = 'validity-text';

/**
 * Valeur booléenne 'true' sous forme de chaîne.
 * @internal
 */
const ARIA_TRUE = 'true';

/**
 * Texte par défaut pour l'état actif.
 * @internal
 */
const TEXT_ACTIVE_DEFAULT =
	BnumConfig.Get('local_keys')?.active_switch ?? 'Activé';

/**
 * Texte par défaut pour l'état inactif.
 * @internal
 */
const TEXT_INACTIVE_DEFAULT =
	BnumConfig.Get('local_keys')?.inactive_switch ?? 'Désactivé';

/**
 * Liste des attributs synchronisés entre l'élément hôte et l'input interne.
 *
 * @remarks
 * Ces attributs sont automatiquement propagés de l'élément personnalisé vers l'input natif.
 * @internal
 */
const SYNCED_ATTRIBUTES = ['name', 'checked', 'value', 'disabled', 'required'];

//#endregion Global Constants

//#region Template

/**
 * Template HTML du composant checkbox.
 *
 * @remarks
 * Structure DOM utilisée pour créer le shadow DOM du composant.
 * Comprend un input checkbox natif configuré en rôle `switch`, un label
 * avec des slots pour le contenu actif/inactif, et une zone d'état de validation.
 *
 * @internal
 */
const TEMPLATE = (
	<HTMLBnumFragment>
		<input id="native-input" type="checkbox" role="switch" />
		<label
			class="checkbox__label label-container hint-label"
			for="native-input"
		>
			<span class="checkbox__label--legend label-container--label ">
				<slot id="legend"></slot>
			</span>
			<span
				id="active-text"
				class="checkbox__label__desc checkbox__label__desc--ok label-container--hint"
			>
				<slot name="activeText">{TEXT_ACTIVE_DEFAULT}</slot>
			</span>
			<span
				id="inactive-text"
				class="checkbox__label__desc checkbox__label__desc--no label-container--hint"
			>
				<slot name="inactiveText">{TEXT_INACTIVE_DEFAULT}</slot>
			</span>
		</label>
		<span class="checkbox__label--hint hint-label label-container--hint">
			<slot id={ID_HINT} name={ID_HINT}></slot>
		</span>
		<div class="checkbox__state state">
			<HTMLBnumIcon id="icon" />
			<span id={ID_VALIDITY_TEXT}></span>
		</div>
	</HTMLBnumFragment>
);

//#endregion Template

/**
 * Composant personnalisé représentant un checkbox avec support de formulaire.
 *
 * @category Input
 *
 * @remarks
 * Ce composant Web étend {@link BnumElementInternal} et fournit un checkbox personnalisé
 * avec support complet des formulaires HTML, gestion d'état, validation et accessibilité.
 *
 * Le composant utilise le Shadow DOM pour encapsuler son style et sa structure,
 * et synchronise automatiquement ses attributs avec un input checkbox natif sous-jacent.
 * Il fonctionne en mode `switch` (interrupteur on/off) avec des textes configurables
 * pour les états actif et inactif.
 *
 * @example
 * Structure simple :
 * ```html
 * <bnum-switch>Click me !</bnum-switch>
 * ```
 *
 * @example
 * Structure avec indice :
 * ```html
 * <bnum-switch>Click me !<span slot="hint">Indice</span></bnum-switch>
 * ```
 *
 * @example
 * Structure required avec helper :
 * ```html
 * <bnum-switch helper required>Click me !<span slot="hint">Indice</span></bnum-switch>
 * ```
 *
 * @fires CustomEvent<BnumCheckBoxDetail> - Déclenché lorsque l'état coché du checkbox change
 *
 * @public
 *
 * @structure Classique
 * <bnum-switch>Click me !</bnum-switch>
 *
 * @structure Avec indice
 * <bnum-switch checked>Click me !<span slot="hint">Indice</span></bnum-switch>
 *
 * @structure Requis
 * <bnum-switch required>Click me !<span slot="hint">Indice</span></bnum-switch>
 *
 * @structure Avec un texte d'aide
 * <bnum-switch helper>Click me !<span slot="hint">Indice</span></bnum-switch>
 *
 * @slot (default) - Légende de l'élément
 * @slot activeText - Texte affiché lorsque le checkbox est activé
 * @slot inactiveText - Texte affiché lorsque le checkbox est désactivé
 * @slot hint - Aide supplémentaire dans la légende
 *
 * @event {CustomEvent<BnumCheckBoxDetail>} change - Lorsque l'élément change d'état
 *
 * @attr {boolean} (optional) (default: false) checked - Si l'élément est coché ou non
 * @attr {string} (optional) name - Nom de l'élément pour les formulaires
 * @attr {string} (optional) (default: 'on') value - Valeur de l'élément
 * @attr {boolean} (optional) (default: false) disabled - Désactive l'élément
 * @attr {boolean} (optional) (default: false) required - Rend le champ obligatoire
 * @attr {boolean} (optional) (default: false) helper - Active le mode d'aide visuelle
 * @attr {boolean} (optional) (default: false) data-no-hint - Désactive les styles lié aux indices
 *
 * @state error - Lorsque la validation échoue
 * @state helper - Lorsque l'attribut helper est actif
 *
 * @cssvar {#ffffff} --bnum-checkbox-idle - Couleur de fond du toggle à l'état inactif
 * @cssvar {#000091} --bnum-checkbox-active - Couleur de fond du toggle à l'état actif
 * @cssvar {#000091} --bnum-checkbox-border-color - Couleur de la bordure
 * @cssvar {1px solid #000091} --bnum-checkbox-border - Bordure complète (largeur, style, couleur)
 * @cssvar {#000091} --bnum-checkbox-icon-active-color - Couleur de l'icône à l'état actif
 * @cssvar {#3a3a3a} --bnum-checkbox-text-idle - Couleur du texte à l'état inactif
 * @cssvar {#3a3a3a} --bnum-checkbox-text-active - Couleur du texte à l'état actif
 * @cssvar {#000091} --bnum-checkbox-text-desc - Couleur du texte de description
 * @cssvar {#ce0500} --bnum-checkbox-error - Couleur de l'état d'erreur
 */
@Define({
	tag: 'bnum-switch',
	template: TEMPLATE,
	styles: [INPUT_BASE_STYLE, INPUT_STYLE_STATES, style],
})
export class HTMLBnumSwitch extends BnumElementInternal {
	//#region Private Fields

	/**
	 * État initial du checkbox lors du chargement.
	 *
	 * @remarks
	 * Stocké lors du préchargement pour pouvoir restaurer l'état initial
	 * lors d'un reset du formulaire via {@link formResetCallback}.
	 *
	 * @internal
	 */
	#_initState!: boolean;

	/**
	 * Références aux éléments du DOM interne.
	 *
	 * @remarks
	 * Injecté automatiquement par le décorateur {@link UI}.
	 * Fournit un accès typé à l'input checkbox natif, aux slots de contenu
	 * et aux éléments d'état de validation.
	 *
	 * @internal
	 */
	@UI({
		input: '#native-input',
		textActive: '#active-text',
		textInactive: '#inactive-text',
		slotLegend: '#legend',
		slotHint: `#${ID_HINT}`,
		validityText: `#${ID_VALIDITY_TEXT}`,
		icon: '#icon',
	})
	accessor #_ui!: Readonly<BnumSwitchUi>;

	//#endregion Private Fields

	//#region Public Fields

	/**
	 * Indique si le checkbox est coché.
	 *
	 * @remarks
	 * Contrôle l'état de sélection du checkbox.
	 *
	 * @defaultValue `false`
	 */
	@Attr() accessor checked = false;

	/**
	 * Le nom du checkbox pour les formulaires.
	 *
	 * @remarks
	 * Permet d'identifier le champ lors de la soumission du formulaire.
	 *
	 * @defaultValue `undefined`
	 */
	@Attr() accessor name: Optional<string> = undefined;

	/**
	 * La valeur associée au checkbox.
	 *
	 * @remarks
	 * Cette valeur est envoyée lors de la soumission du formulaire si le checkbox est coché.
	 *
	 * @defaultValue `'on'`
	 */
	@Attr() accessor value: string = 'on';

	/**
	 * Indique si le checkbox est désactivé.
	 *
	 * @remarks
	 * Un checkbox désactivé ne peut pas être sélectionné ni recevoir le focus.
	 *
	 * @defaultValue `false`
	 */
	@Attr() accessor disabled = false;

	/**
	 * Indique si le checkbox est obligatoire.
	 *
	 * @remarks
	 * Un checkbox obligatoire doit être coché pour que le formulaire soit valide.
	 *
	 * @defaultValue `false`
	 */
	@Attr() accessor required = false;

	/**
	 * Active le mode d'aide visuelle.
	 *
	 * @remarks
	 * Lorsque activé, ajoute l'état `helper` au composant pour un rendu visuel spécifique.
	 *
	 * @defaultValue `false`
	 */
	@Attr() accessor helper = false;

	/**
	 * Texte de la légende principale du checkbox.
	 *
	 * @remarks
	 * Stocke le contenu textuel provenant de l'attribut `data-legend`.
	 * Utilisé pour initialiser le slot de légende si défini.
	 *
	 * @defaultValue `undefined`
	 * @internal
	 */
	@Data() accessor #_legend: Optional<string>;

	/**
	 * Texte de l'indice/aide du checkbox.
	 *
	 * @remarks
	 * Stocke le contenu textuel provenant de l'attribut `data-hint`.
	 * Utilisé pour initialiser le slot d'indice si défini.
	 *
	 * @defaultValue `undefined`
	 * @internal
	 */
	@Data() accessor #_hint: Optional<string>;

	/**
	 * Supprime les styles lié aux indices/aide.
	 *
	 * @remarks
	 * Ne permet plus d'en mettre ensuite.
	 *
	 * @default false
	 * @internal
	 */
	@Data('no-hint') accessor #_noHint: Optional<boolean>;

	/**
	 * Événement personnalisé déclenché lors du changement d'état coché.
	 *
	 * @remarks
	 * Initialisé par {@link OnCheckedChangeInitializer} via le décorateur {@link Listener}.
	 * Permet de s'abonner aux changements d'état du checkbox.
	 */
	@Listener(OnCheckedChangeInitializer)
	accessor oncheckedchange!: OnCheckedChangeEvent;

	//#endregion Public Fields

	//#region Lifecycle

	/**
	 * Constructeur du composant HTMLBnumCheckbox.
	 *
	 * @remarks
	 * Initialise l'instance du composant en appelant le constructeur parent.
	 */
	constructor() {
		super();
	}

	/**
	 * Précharge l'état initial du checkbox.
	 *
	 * @remarks
	 * Sauvegarde l'état coché initial pour permettre la restauration
	 * lors d'un reset de formulaire.
	 *
	 * @protected
	 * @override
	 */
	protected _p_preload(): void {
		this.#_initState = !!(this.checked || false);
	}

	/**
	 * Attache le composant au DOM et initialise son comportement.
	 *
	 * @remarks
	 * Initialise les données des slots, synchronise les attributs avec l'input natif,
	 * vérifie la présence d'un label et gère l'état d'erreur initial.
	 * Ajoute l'état `helper` si l'attribut correspondant est défini.
	 *
	 * @protected
	 * @override
	 */
	protected _p_attach(): void {
		this.#_init().#_sync().#_checkLabel().#_ifOnErrorSet();

		if (this.helper) this._p_addState(ATTRIBUTE_HELPER);
	}

	/**
	 * Gère la mise à jour d'un attribut observé.
	 *
	 * @param name - Le nom de l'attribut modifié
	 * @param oldVal - L'ancienne valeur de l'attribut
	 * @param newVal - La nouvelle valeur de l'attribut
	 * @returns `void` ou `'break'` pour interrompre le traitement
	 *
	 * @remarks
	 * Cette méthode est appelée automatiquement lorsqu'un attribut observé change.
	 * Elle traite spécifiquement les attributs `checked` et `helper`,
	 * et délègue les autres attributs à l'input natif.
	 *
	 * @protected
	 * @override
	 */
	protected _p_update(
		name: string,
		oldVal: string | null,
		newVal: string | null,
	): void | Nullable<'break'> {
		if (newVal === EMPTY_STRING) newVal = ARIA_TRUE;

		if (oldVal === newVal) return;

		switch (name) {
			case ATTRIBUTE_CHECKED:
				if (this.#_ui.input.checked !== this.checked) {
					this.#_ui.input.checked = this.checked;
					this.#_ui.input.setAttribute(ARIA_CHECKED, String(this.checked));
				}
				break;

			case ATTRIBUTE_HELPER:
				if (newVal) this._p_addState(ATTRIBUTE_HELPER);
				else this._p_removeState(ATTRIBUTE_HELPER);
				break;

			default:
				if (newVal) this.#_ui.input.setAttribute(name, newVal);
				else this.#_ui.input.removeAttribute(name);
				break;
		}
	}

	/**
	 * Effectue les opérations post-flush du composant.
	 *
	 * @remarks
	 * Vérifie l'état d'erreur et resynchronise les attributs après un flush.
	 *
	 * @protected
	 * @override
	 */
	protected _p_postFlush(): void {
		this.#_ifOnErrorSet().#_sync();
	}

	/**
	 * Callback de réinitialisation du formulaire.
	 *
	 * @remarks
	 * Restaure l'état coché initial du checkbox lorsque le formulaire est réinitialisé.
	 */
	formResetCallback(): void {
		this.checked = this.#_initState;
	}

	/**
	 * Active ou désactive le champ selon l'état du fieldset parent.
	 *
	 * @param disabled - `true` pour désactiver, `false` pour activer
	 */
	formDisabledCallback(disabled: boolean): void {
		this.disabled = disabled;
	}

	/**
	 * Met à jour l'état coché du checkbox et déclenche l'événement de changement.
	 *
	 * @param checked - L'état coché à définir
	 *
	 * @remarks
	 * Cette méthode est utilisée en interne pour mettre à jour l'état coché
	 * et déclencher l'événement de changement correspondant.
	 */
	updateCheckedAndFire(checked: boolean): void {
		this.checked = checked;
		this.#_change(new Event('change'));
	}

	//#endregion Lifecycle

	//#region Public Methods

	/**
	 * Vérifie la validité du checkbox sans afficher de message.
	 *
	 * @returns `true` si le checkbox est valide, `false` sinon
	 *
	 * @remarks
	 * Délègue la vérification à l'input natif sous-jacent.
	 * En cas d'erreur, retourne `true` par défaut.
	 */
	checkValidity(): boolean {
		return this.#_checkValidity().unwrapOr(true);
	}

	/**
	 * Vérifie la validité du checkbox et affiche le message de validation.
	 *
	 * @returns `true` si le checkbox est valide, `false` sinon
	 *
	 * @remarks
	 * Délègue la vérification à l'input natif sous-jacent et déclenche
	 * l'affichage du message de validation natif si invalide.
	 * En cas d'erreur, retourne `true` par défaut.
	 */
	reportValidity(): boolean {
		return this.#_reportValidity().unwrapOr(true);
	}

	//#endregion Public Methods

	//#region Private Methods

	/**
	 * Vérifie la validité de l'input natif sans rapport.
	 *
	 * @returns Un {@link Result} contenant le résultat de la vérification
	 *
	 * @private
	 */
	@Risky()
	#_checkValidity(): Result<boolean> {
		return this.#_ui.input.checkValidity() as unknown as Result<boolean>;
	}

	/**
	 * Vérifie la validité de l'input natif avec rapport.
	 *
	 * @returns Un {@link Result} contenant le résultat de la vérification
	 *
	 * @private
	 */
	@Risky()
	#_reportValidity(): Result<boolean> {
		return this.#_ui.input.reportValidity() as unknown as Result<boolean>;
	}

	/**
	 * Initialise les données internes du composant.
	 *
	 * @returns L'instance courante pour chaînage de méthodes
	 *
	 * @remarks
	 * Initialise les données des slots de légende et d'indice,
	 * puis configure les écouteurs d'événements.
	 *
	 * @private
	 */
	#_init() {
		return this.#_initDataLegend().#_initDataHint().#_setListeners();
	}

	/**
	 * Initialise le slot de légende avec la donnée correspondante.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @private
	 */
	#_initDataLegend() {
		return this.#_initData(this.#_legend, this.#_ui.slotLegend);
	}

	/**
	 * Initialise le slot d'indice avec la donnée correspondante.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @private
	 */
	#_initDataHint() {
		if (this.#_noHint) {
			this._p_addState('no-hint');
			this.#_hint = undefined;
			return this;
		}

		return this.#_initData(this.#_hint, this.#_ui.slotHint);
	}

	/**
	 * Initialise un slot avec une donnée textuelle.
	 *
	 * @param data - La donnée textuelle à insérer dans le slot
	 * @param slot - Le slot cible dans lequel insérer la donnée
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * N'affecte le slot que si la donnée est définie et non vide.
	 *
	 * @private
	 */
	#_initData(data: MayBe<string>, slot: HTMLSlotElement): this {
		if (data) slot.textContent = data;

		return this;
	}

	/**
	 * Configure tous les écouteurs d'événements du composant.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * Actuellement configure uniquement l'écouteur de changement de l'input interne.
	 *
	 * @private
	 */
	#_setListeners() {
		return this.#_listenChange();
	}

	/**
	 * Configure l'écouteur d'événement pour les changements de l'input interne.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * Écoute l'événement `change` de l'input natif et déclenche
	 * la méthode {@link #_change} pour propager le changement.
	 *
	 * @private
	 */
	#_listenChange() {
		this.#_ui.input.addEventListener(EVENT_CHANGE, e => {
			this.#_change(e);
		});

		return this;
	}

	/**
	 * Traite le changement d'état du checkbox et déclenche l'événement personnalisé.
	 *
	 * @param event - L'événement natif de changement
	 * @returns Les détails de l'événement contenant la référence à l'instance
	 *
	 * @remarks
	 * Met à jour la propriété `checked` à partir de l'input natif,
	 * puis retourne les détails nécessaires au décorateur {@link Fire}
	 * pour dispatcher l'événement `change`.
	 *
	 * @fires CustomEvent<BnumCheckBoxDetail>
	 * @private
	 */
	@Fire(EVENT_CHANGE)
	#_change(event: Event): BnumSwitchDetail {
		this.checked = this.#_ui.input.checked;
		return { inner: event, caller: this };
	}

	/**
	 * Vérifie et applique l'état d'erreur si nécessaire.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * Si la validation échoue, applique l'état d'erreur via {@link #_setOnError}.
	 * Si la validation réussit et que l'état d'erreur est actif,
	 * le supprime et réinitialise la validité.
	 *
	 * @private
	 */
	#_ifOnErrorSet() {
		if (!this.checkValidity()) {
			this.#_setOnError();
		} else if (this._p_hasState(STATE_ERROR)) {
			this._p_removeStates([STATE_STATE, STATE_ERROR]).#_setValidity();
			this.#_ui.input.removeAttribute(ARIA_INVALID);
		}

		return this;
	}

	/**
	 * Applique l'état d'erreur au composant.
	 *
	 * @remarks
	 * Configure l'icône d'erreur, met à jour le texte de validité,
	 * ajoute les états internes `state` et `error`, et positionne
	 * l'attribut ARIA `aria-invalid` à `true`.
	 *
	 * Si le texte de validation natif n'est pas disponible, utilise
	 * le message par défaut {@link DEFAULT_VALIDITY_MESSAGE}.
	 *
	 * @private
	 */
	#_setOnError() {
		if (this.#_ui.icon.icon !== BnumSwitchIcon.ERROR)
			this.#_ui.icon.icon = BnumSwitchIcon.ERROR;
		this.#_setText()
			.tapError(() => {
				this.#_ui.validityText.textContent = DEFAULT_VALIDITY_MESSAGE;
			})
			.andThen(() => this.#_setInternalError());
		this._p_addStates(STATE_STATE, STATE_ERROR);
		this.#_ui.input.setAttribute(ARIA_INVALID, ARIA_TRUE);
	}

	/**
	 * Met à jour le texte de validité à partir du message natif de l'input.
	 *
	 * @returns Un {@link Result} indiquant le succès de l'opération
	 *
	 * @remarks
	 * Ne met à jour le texte que si il diffère du message de validation courant.
	 *
	 * @private
	 */
	@Risky()
	#_setText(): Result<void> {
		if (
			this.#_ui.validityText.textContent !== this.#_ui.input.validationMessage
		)
			this.#_ui.validityText.textContent = this.#_ui.input.validationMessage;
		return null as unknown as Result<void>;
	}

	/**
	 * Propage l'état de validité de l'input natif vers les internals du composant.
	 *
	 * @returns Un {@link Result} indiquant le succès de l'opération
	 *
	 * @remarks
	 * Utilise l'API `ElementInternals.setValidity` pour synchroniser
	 * la validité du composant avec celle de l'input natif sous-jacent.
	 *
	 * @private
	 */
	@Risky()
	#_setInternalError(): Result<void> {
		if (!this.checkValidity()) {
			this._p_internal.setValidity(
				this.#_ui.input.validity,
				this.#_ui.input.validationMessage,
				this.#_ui.input,
			);
		}

		return null as unknown as Result<void>;
	}

	/**
	 * Réinitialise la validité du composant.
	 *
	 * @returns Un {@link Result} indiquant le succès de l'opération
	 *
	 * @remarks
	 * Supprime l'état d'erreur des internals lorsque le checkbox redevient valide.
	 *
	 * @private
	 */
	@Risky()
	#_setValidity(): Result<void> {
		if (this.checkValidity()) {
			this._p_internal.setValidity({});
		}

		return null as unknown as Result<void>;
	}

	/**
	 * Synchronise les attributs entre l'élément hôte et l'input interne.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * Parcourt tous les {@link SYNCED_ATTRIBUTES} et applique leurs valeurs à l'input.
	 * Cas particulier : l'attribut `checked` est traité en tant que propriété booléenne.
	 * Après la synchronisation des attributs, met à jour les attributs ARIA.
	 *
	 * @private
	 */
	#_sync() {
		for (const attr of SYNCED_ATTRIBUTES) {
			if (attr === ATTRIBUTE_CHECKED) {
				this.#_ui.input.checked = this.checked;
			} else {
				if (this.hasAttribute(attr)) {
					this.#_ui.input.setAttribute(attr, this.getAttribute(attr)!);
				} else this.#_ui.input.removeAttribute(attr);
			}
		}

		this.#_syncAria();

		return this;
	}

	/**
	 * Synchronise les attributs ARIA du composant avec l'input interne.
	 *
	 * @remarks
	 * Met à jour les attributs `aria-checked`, `aria-required`, `aria-disabled`
	 * et `aria-describedby` en fonction de l'état courant du composant.
	 *
	 * L'attribut `aria-describedby` est composé dynamiquement à partir
	 * des éléments d'aide et de validité présents.
	 *
	 * @private
	 */
	#_syncAria() {
		const input = this.#_ui.input;

		input.setAttribute(ARIA_CHECKED, String(this.checked));

		if (this.required) input.setAttribute(ARIA_REQUIRED, ARIA_TRUE);
		else input.removeAttribute(ARIA_REQUIRED);

		if (this.disabled) input.setAttribute(ARIA_DISABLED, ARIA_TRUE);
		else input.removeAttribute(ARIA_DISABLED);

		const descriptions: string[] = [];

		if (this.#_ui.slotHint.assignedNodes().length > 0)
			descriptions.push(ID_HINT);

		if (this.#_ui.validityText.textContent) descriptions.push(ID_VALIDITY_TEXT);

		if (descriptions.length > 0)
			input.setAttribute(ARIA_DESCRIBEDBY, descriptions.join(' '));
		else input.removeAttribute(ARIA_DESCRIBEDBY);
	}

	/**
	 * Vérifie la présence d'un label accessible et affiche un avertissement sinon.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * Vérifie si une légende ou un indice est disponible pour le composant.
	 * Si aucun texte accessible n'est trouvé, émet un avertissement dans la console.
	 *
	 * @private
	 */
	#_checkLabel() {
		const hasLabel = this.#_verifyLabel();

		if (!hasLabel) {
			Log.warn(LOG_TAG, WARN_NO_LABEL);
		}

		return this;
	}

	/**
	 * Vérifie si au moins une source de label est disponible.
	 *
	 * @returns `true` si une légende ou un indice existe, `false` sinon
	 *
	 * @private
	 */
	#_verifyLabel() {
		return this.#_verifyLegend() || this.#_verifyHint();
	}

	/**
	 * Vérifie la présence d'une légende.
	 *
	 * @returns `true` si une légende est définie ou si des éléments enfants existent
	 *
	 * @private
	 */
	#_verifyLegend() {
		return this.#_verifyData(this.#_legend);
	}

	/**
	 * Vérifie la présence d'un indice.
	 *
	 * @returns `true` si un indice est défini ou si des éléments slottés existent
	 *
	 * @private
	 */
	#_verifyHint() {
		return this.#_verifyData(this.#_hint, ID_HINT);
	}

	/**
	 * Vérifie la disponibilité d'une donnée ou d'éléments enfants associés.
	 *
	 * @param data - La donnée textuelle à vérifier
	 * @param slotName - Le nom du slot à inspecter (null pour le slot par défaut)
	 * @returns `true` si la donnée est définie ou si des éléments enfants existent
	 *
	 * @private
	 */
	#_verifyData(data: MayBe<string>, slotName: MayBe<string> = null) {
		const hasData = !!data;
		const hasElements = this.#_verifyElements(slotName);
		return hasData || hasElements;
	}

	/**
	 * Vérifie la présence d'éléments enfants dans un slot donné.
	 *
	 * @param slotName - Le nom du slot à inspecter (null pour les enfants sans slot)
	 * @returns `true` si au moins un élément est trouvé
	 *
	 * @private
	 */
	#_verifyElements(slotName: MayBe<string>) {
		const iterator = this.#_getVerifyElements(slotName);
		return !iterator.next().done;
	}

	/**
	 * Générateur produisant les éléments enfants d'un slot donné.
	 *
	 * @param slotName - Le nom du slot à inspecter (null pour les enfants sans slot)
	 * @yields Les éléments enfants correspondant au critère de slot
	 *
	 * @remarks
	 * Si un nom de slot est fourni, retourne les éléments ayant l'attribut `slot` correspondant.
	 * Sinon, retourne les éléments enfants sans attribut `slot` et les nœuds texte non vides.
	 *
	 * @private
	 */
	*#_getVerifyElements(slotName: MayBe<string>) {
		if (slotName)
			yield* Array.from(this.querySelectorAll(`[slot="${slotName}"]`));
		else {
			const nodes = [...Array.from(this.childNodes)];
			for (const node of nodes) {
				if (node instanceof HTMLElement) {
					if (!node.hasAttribute('slot')) yield node;
				} else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
					yield node;
			}
		}
	}

	//#endregion Private Methods

	//#region Static Methods

	/**
	 * Retourne la liste des attributs observés par le composant.
	 *
	 * @returns Un tableau contenant tous les noms d'attributs observés
	 *
	 * @remarks
	 * Combine les attributs observés du parent avec les {@link SYNCED_ATTRIBUTES}
	 * et l'attribut `helper` spécifiques à ce composant.
	 * Les changements de ces attributs déclencheront {@link _p_update}.
	 *
	 * @protected
	 * @static
	 * @override
	 * @deprecated Utilisez le décorateur {@link Observe} du commit 3e38db0162eef596874dbe32490d9e96b09fb1c0
	 * @see [feat(composants): ✨ Ajout d'un décorateur pour réduire le boilerplate des attibuts à observer](https://github.com/messagerie-melanie2/design-system-bnum/commit/3e38db0162eef596874dbe32490d9e96b09fb1c0)
	 */
	@NonStd('Deprecated')
	protected static _p_observedAttributes(): string[] {
		return [
			...super._p_observedAttributes(),
			...SYNCED_ATTRIBUTES,
			ATTRIBUTE_HELPER,
		];
	}

	/**
	 * Indique que ce composant peut être associé à un formulaire.
	 *
	 * @remarks
	 * Permet au composant de participer au cycle de vie des formulaires HTML,
	 * notamment la soumission, la validation et la réinitialisation.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/ElementInternals#instance_properties | ElementInternals}
	 */
	static get formAssociated(): Readonly<boolean> {
		return true;
	}

	//#endregion Static Methods
}
